// libraries
import React, { useState, useImperativeHandle, forwardRef, useId } from 'react'
import clsx from 'clsx'

// utils
import { isNonWhitespaceString } from '/lib/reactutils/utils.js'

// svg
import { AngleDown } from '/react4xp/components/Svg/Ux/Angle'

export interface AccordionProps {
	title: string
	children: React.ReactNode
	className?: string
	headingLevel?: 2 | 3 | 4 | 5 | 6
}

function Accordion({
	title,
	children,
	className,
	headingLevel = 2
}: AccordionProps, ref) {

	// the trigger carries the section title, so it belongs inside a heading —
	// the level depends on where the page puts the accordion
	const Heading = `h${headingLevel}` as const

	const [isActive, setIsActive] = useState(false)

	// unique per instance, several accordions can live on the same page
	const instanceId = useId()
	const titleId = `${instanceId}-title`
	const panelId = `${instanceId}-panel`

    const toggleState = () => {
        setIsActive(prevState => !prevState)
    }

	// expose toggleState and isActive through the ref
	useImperativeHandle(ref, () => ({
		toggle: toggleState,
		open: () => setIsActive(true),
		close: () => setIsActive(false),
		isActive,
	}))

	return (
		<div
			className={clsx(
				'accordion-component',
				isActive && 'accordion-component--active',
				className
			)}
		>

            <Heading className='accordion-component__heading'>

                <button
                    type='button'
                    id={titleId}
                    className='accordion-component__title'
                    aria-expanded={isActive}
                    aria-controls={panelId}
                    onClick={toggleState}
                >

                    {isNonWhitespaceString(title) && (
                        <span className='text'>
                            {title}
                        </span>
                    )}

                    <AngleDown />

                </button>

            </Heading>

            <div
				id={panelId}
				className='accordion-component__content'
				role='region'
				aria-labelledby={titleId}
			>
                <div>
                    <div>
                        {children}
                    </div>
                </div>
            </div>

        </div>
	)
}

export default forwardRef(Accordion)